/**
 * Which slice of the calendar Ontdek asks for.
 *
 * The window is the only thing that bounds this screen. `GET /project/employee-day-range` accepts a
 * `pagination` argument and never reads it — see `getEmployeeDayRange` in the api, which ends on
 * `new PaginatedProjectEmployeeDayRange(items, items.length)` after sorting the whole result. So
 * the server returns every opportunity between the two dates it is given, and the `take: 1000` the
 * app used to send did nothing at all.
 *
 * The screen used to offer four period chips — this week, the weekend, next month, and a rolling
 * six weeks. Four chips answer four questions somebody thought of in advance; the employee who
 * wants to know what is on in the week of 12 October could not ask it. That employee is the one
 * worth having, because they plan ahead. A month you page through answers every question of that
 * shape with the same control.
 *
 * De kalender is sindsdien een springplank en geen filter: Ontdek haalt alles op wat openstaat
 * (`openDiscoverWindow`) en de kalender brengt je ernaartoe. `discoverFetchWindow` bestaat nog voor
 * Diensten, dat een venster om jouw eigen agenda vraagt.
 *
 * ## Days are strings here, deliberately
 *
 * A card's day arrives as `2026-08-14T00:00:00.000Z` and the list groups on `day.split("T")[0]`. If
 * the calendar built its grid from `Date` objects it would derive those same keys through the
 * device's timezone, and west of UTC every cell would point at the day before. So the grid is built
 * from plain integer arithmetic and compared as `YYYY-MM-DD` strings — the one representation that
 * cannot drift. `Date` appears only where it has to: the range handed to the server.
 */

/**
 * How many months past the visible one are fetched along with it.
 *
 * Paging into an empty month is a dead end the screen hands you itself, so the calendar has to know
 * which days hold work before you travel there — and that means fetching wider than you show. Two
 * months is enough to keep the marks ahead of the reader without asking the server for a year.
 */
export const DISCOVER_LOOKAHEAD_MONTHS = 2;

export type DiscoverWindow = { from: Date; to: Date };

/** A month, as the two numbers that identify it. `month` is 0-indexed, like `Date`. */
export type YearMonth = { year: number; month: number };

/** `YYYY-MM-DD`, the same shape `card.day.split("T")[0]` produces. */
export type DayKey = string;

export function dayKey(year: number, month: number, day: number): DayKey {
  return `${year}-${pad(month + 1)}-${pad(day)}`;
}

/** Today, in the timezone the person holding the phone lives in — which is the one they mean. */
export function todayKey(now: Date): DayKey {
  return dayKey(now.getFullYear(), now.getMonth(), now.getDate());
}

export function monthOf(key: DayKey): YearMonth {
  const [year, month] = key.split("-").map(Number);
  return { year, month: month - 1 };
}

export function addMonths({ year, month }: YearMonth, count: number): YearMonth {
  const total = year * 12 + month + count;
  return { year: Math.floor(total / 12), month: ((total % 12) + 12) % 12 };
}

export function daysInMonth({ year, month }: YearMonth): number {
  return new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
}

/** Monday = 0, so the grid reads the way a Dutch calendar does. */
export function firstWeekdayOfMonth({ year, month }: YearMonth): number {
  return (new Date(Date.UTC(year, month, 1)).getUTCDay() + 6) % 7;
}

/** Every day in the month, in order. */
export function monthDays(target: YearMonth): DayKey[] {
  return Array.from({ length: daysInMonth(target) }, (_, index) =>
    dayKey(target.year, target.month, index + 1),
  );
}

/** The seven days of the week `key` falls in, Monday first — the strip you see when collapsed. */
export function weekOf(key: DayKey): DayKey[] {
  const [year, month, day] = key.split("-").map(Number);
  const monday = new Date(Date.UTC(year, month - 1, day));
  monday.setUTCDate(monday.getUTCDate() - ((monday.getUTCDay() + 6) % 7));

  return Array.from({ length: 7 }, (_, index) => {
    const date = new Date(monday);
    date.setUTCDate(monday.getUTCDate() + index);
    return dayKey(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
  });
}

export function shiftDays(key: DayKey, count: number): DayKey {
  const [year, month, day] = key.split("-").map(Number);
  const date = new Date(Date.UTC(year, month - 1, day + count));
  return dayKey(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
}

/**
 * The same day number a month over, held to a month that actually has it — 31 January + 1 is 28
 * February.
 */
export function shiftMonths(key: DayKey, count: number): DayKey {
  const { year, month } = monthOf(key);
  const target = addMonths({ year, month }, count);
  const day = Number(key.split("-")[2]);

  return dayKey(target.year, target.month, Math.min(day, daysInMonth(target)));
}

export function lastDayOfMonth(target: YearMonth): DayKey {
  return dayKey(target.year, target.month, daysInMonth(target));
}

/**
 * What gets fetched: today up to and including the month the calendar is standing in, plus the
 * lookahead the day marks need.
 *
 * **It always starts at today, whatever month you page to.** The calendar is a jump control, not a
 * filter: tapping 3 September scrolls the list to 3 September, and scrolling back up must still
 * land on tomorrow. A window that moved its near edge along with the anchor would delete the days
 * above you the moment you paged forward — you would arrive in September and find August gone from
 * under your thumb.
 *
 * So the window only ever grows, forward. Paging on widens the far end and refetches; nothing that
 * was in the list leaves it.
 */
export function discoverFetchWindow(
  target: YearMonth,
  now: Date,
  lookahead = DISCOVER_LOOKAHEAD_MONTHS,
): DiscoverWindow {
  const ahead = addMonths(target, lookahead);

  return {
    from: startOf(todayKey(now)),
    to: endOf(dayKey(ahead.year, ahead.month, daysInMonth(ahead))),
  };
}

/**
 * Alles wat openstaat: vanaf vandaag, twee jaar vooruit.
 *
 * Dit is wat Ontdek ophaalt, altijd. Er heeft een tijd een vlag omheen gestaan
 * (`appReworkDiscoverWindowEnabled`) die het terugbracht tot de zichtbare maand plus twee -- goed
 * voor de snelheid, maar het besliste ook _welke projecten een medewerker kan zien_, en dat is geen
 * knop die aan of uit hoort te staan. Wie een dienst openzet wil dat hij gevonden wordt, ook als
 * hij vijf maanden vooruit staat.
 *
 * Twee jaar is de praktische betekenis van "alles": verder vooruit plant niemand, en het is
 * dezelfde grens die dit scherm had voordat het venster bestond.
 *
 * Wat het kost, voor wie het later terug wil brengen: het is één verzoek dat elke kaart met naam,
 * afbeelding en tijdvakken teruggeeft. `GET /project/employee-day-range` negeert zijn
 * `pagination`-argument, dus de grens ligt hier en nergens anders.
 */
export function openDiscoverWindow(now: Date): DiscoverWindow {
  const twoYears = new Date(now);
  twoYears.setFullYear(twoYears.getFullYear() + 2);

  return { from: startOf(todayKey(now)), to: endOf(todayKey(twoYears)) };
}

function startOf(key: DayKey): Date {
  return new Date(`${key}T00:00:00.000Z`);
}

function endOf(key: DayKey): Date {
  return new Date(`${key}T23:59:59.999Z`);
}

function pad(value: number): string {
  return String(value).padStart(2, "0");
}
